import { clamp } from './utils.js';

/**
 * @param {number} steps
 * @param {number} pulses
 * @param {number} [numRounds]
 * @returns {number[]}
 */
export function buildPattern(steps, pulses, numRounds) {
    steps = steps | 0;
    pulses = pulses | 0;
    if (steps <= 0) return [];
    if (pulses < 0 || pulses > steps) return [];

    const maxRounds = rawRoundCount(steps, pulses);
    const effectiveRounds =
        numRounds === undefined ? maxRounds : clamp(numRounds | 0, 0, maxRounds);

    if (effectiveRounds === 0) {
        return buildInitialPattern(steps, pulses);
    }

    if (effectiveRounds === maxRounds && (pulses === 0 || steps % pulses === 0)) {
        return buildRegularPattern(steps, pulses);
    }

    /** @type {number[][]} */
    const sequence = Array.from({ length: pulses }, () => [1]);
    /** @type {number[][]} */
    const remainder = Array.from({ length: steps - pulses }, () => [0]);

    return spread(sequence, remainder, effectiveRounds);
}

/**
 * @param {number[][]} sequence
 * @param {number[][]} remainder
 * @param {number} roundLimit
 * @param {number} round
 * @returns {number[]}
 */
function spread(sequence, remainder, roundLimit, round = 0) {
    if (remainder.length === 0) return sequence.flat();
    if (round >= roundLimit) return [...sequence.flat(), ...remainder.flat()];

    /** @type {number[][]} */
    const nextSequence = [];

    while (remainder.length > 0) {
        if (sequence.length === 0) {
            return spread(nextSequence, remainder, roundLimit, round + 1);
        }

        const rem = remainder.pop();
        if (rem === undefined) break;
        const current = sequence[sequence.length - 1];
        if (current === undefined) break;

        current.push(...rem);
        const moved = sequence.pop();
        if (moved !== undefined) nextSequence.push(moved);
    }

    return spread(nextSequence, sequence, roundLimit, round + 1);
}

/** @param {number} steps @param {number} pulses @returns {number[]} */
function buildRegularPattern(steps, pulses) {
    if (pulses === 0) return new Array(steps).fill(0);

    const gap = steps / pulses;
    /** @type {number[]} */
    const out = [];

    for (let i = 0; i < pulses; i++) {
        out.push(1);
        for (let j = 1; j < gap; j++) out.push(0);
    }

    return out;
}

/** @param {number} steps @param {number} pulses @returns {number[]} */
function buildInitialPattern(steps, pulses) {
    /** @type {number[]} */
    const out = [];
    for (let i = 0; i < steps; i++) out.push(i < pulses ? 1 : 0);
    return out;
}

/** @param {number} sequenceLen @param {number} remainderLen @returns {number} */
function spreadRoundCount(sequenceLen, remainderLen) {
    if (remainderLen === 0) return 0;

    let nextSequenceLen = 0;
    while (remainderLen > 0) {
        if (sequenceLen === 0) {
            return 1 + spreadRoundCount(nextSequenceLen, remainderLen);
        }
        sequenceLen -= 1;
        remainderLen -= 1;
        nextSequenceLen += 1;
    }

    return 1 + spreadRoundCount(nextSequenceLen, sequenceLen);
}

/** @param {number} steps @param {number} pulses @returns {number} */
export function maxRoundCount(steps, pulses) {
    steps = steps | 0;
    pulses = pulses | 0;
    if (steps <= 0) return 0;
    if (pulses < 0 || pulses > steps) return 0;
    if (pulses <= 1) return 0;
    const rawMax = rawRoundCount(steps, pulses);
    if (pulses <= 0 || pulses >= steps) return rawMax;
    if (gcd(steps, pulses) === 1) return Math.max(0, rawMax - 1);
    return rawMax;
}

/** @param {number} steps @param {number} pulses @returns {number} */
function rawRoundCount(steps, pulses) {
    return spreadRoundCount(pulses, steps - pulses);
}

/** @param {number[]} pattern @param {number} r @returns {number[]} */
export function rotatePattern(pattern, r) {
    const n = pattern.length;
    if (n === 0) return [];
    const out = new Array(n);
    for (let i = 0; i < n; i++) {
        out[i] = pattern[mod(i - r, n)];
    }
    return out;
}

/** @template T @param {T[]} a @returns {T[]} */
export function reverseArray(a) {
    const out = a.slice();
    out.reverse();
    return out;
}

/** @param {number} n @param {number} m */
export function mod(n, m) {
    return ((n % m) + m) % m;
}

/** @param {number} a @param {number} b */
export function gcd(a, b) {
    a = Math.abs(a);
    b = Math.abs(b);
    while (b !== 0) {
        const t = b;
        b = a % b;
        a = t;
    }
    return a;
}

/** @param {number} steps @param {number} pulses @param {number} [numRounds] */
export function modeCount(steps, pulses, numRounds) {
    const safeSteps = Math.max(0, steps | 0);
    const safePulses = clamp(pulses | 0, 0, safeSteps);
    const roundsMax = maxRoundCount(safeSteps, safePulses);
    if (numRounds !== undefined) {
        const rounds = clamp(numRounds | 0, 0, roundsMax);
        if (rounds < roundsMax) return safeSteps;
    }
    if (safePulses === 0) return 1;
    return safeSteps / gcd(safeSteps, safePulses);
}

const reverseOffsetCache = new Map();

/** @param {number} steps @param {number} pulses */
export function reverseOffset(steps, pulses) {
    const m = modeCount(steps, pulses);
    if (m === 1) return 0;
    const key = `${steps}|${pulses}`;
    const cached = reverseOffsetCache.get(key);
    if (cached !== undefined) return cached;

    const base = buildPattern(steps, pulses);
    const rev = reverseArray(base);
    let k = 0;
    for (let i = 0; i < m; i++) {
        const candidate = rotatePattern(base, i);
        if (arraysEqual(candidate, rev)) {
            k = i;
            break;
        }
    }
    reverseOffsetCache.set(key, k);
    return k;
}

/** @template T @param {T[]} a @param {T[]} b */
export function arraysEqual(a, b) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
    }
    return true;
}

/**
 * @param {number} steps
 * @param {number} secondaryPulses
 * @param {number} secondaryMode
 * @param {number} primaryPulses
 * @param {number} primaryMode
 * @param {number} [secondaryRounds]
 * @param {number} [primaryRounds]
 * @returns {number[]}
 */
export function buildStressLevels(
    steps,
    secondaryPulses,
    secondaryMode,
    primaryPulses,
    primaryMode,
    secondaryRounds,
    primaryRounds,
) {
    const base = rotatePattern(
        buildPattern(steps, secondaryPulses, secondaryRounds),
        secondaryMode,
    );
    const levels = new Array(steps).fill(0);

    /** @type {number[]} */
    const pulseSteps = [];
    for (let i = 0; i < base.length; i++) {
        if (base[i] === 1) pulseSteps.push(i);
    }

    for (const s of pulseSteps) levels[s] = 1;

    const p1 = pulseSteps.length;
    const p2 = clamp(primaryPulses | 0, 0, Math.max(0, p1));

    if (p1 > 0 && p2 > 0) {
        const inner = rotatePattern(buildPattern(p1, p2, primaryRounds), primaryMode);
        for (let i = 0; i < p1; i++) {
            const step = pulseSteps[i];
            if (step !== undefined && inner[i] === 1) levels[step] = 2;
        }
    }

    return levels;
}
